import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  open: boolean;
  selectedDateIso: string;
  onClose: () => void;
  onSelect: (iso: string) => void;
};

function parseIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d);
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'] as const;

const PointItemDateCalendarModal: React.FC<Props> = ({
  open,
  selectedDateIso,
  onClose,
  onSelect,
}) => {
  const selected = useMemo(() => parseIso(selectedDateIso), [selectedDateIso]);
  const [viewMonth, setViewMonth] = useState(() => new Date(selected.getFullYear(), selected.getMonth(), 1));
  const [editingYm, setEditingYm] = useState(false);
  const [tempYear, setTempYear] = useState('');
  const [tempMonth, setTempMonth] = useState('');

  useEffect(() => {
    if (!open) return;
    setViewMonth(new Date(selected.getFullYear(), selected.getMonth(), 1));
    setEditingYm(false);
  }, [open, selected]);

  if (!open) return null;

  const today = new Date();
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const finishYmEdit = () => {
    const y = Number.parseInt(tempYear, 10);
    const m = Number.parseInt(tempMonth, 10) - 1;
    if (y >= 1900 && y <= 2100 && m >= 0 && m <= 11) {
      setViewMonth(new Date(y, m, 1));
    }
    setEditingYm(false);
  };

  const cells: React.ReactNode[] = [];
  for (let i = 0; i < firstDow; i += 1) {
    cells.push(<div key={`e-${i}`} className="cp-pin-cal-day is-empty" />);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const isToday =
      year === today.getFullYear() && month === today.getMonth() && day === today.getDate();
    const isSelected =
      year === selected.getFullYear() &&
      month === selected.getMonth() &&
      day === selected.getDate();
    const isSunday = new Date(year, month, day).getDay() === 0;
    cells.push(
      <button
        key={day}
        type="button"
        className={`cp-pin-cal-day${isToday ? ' is-today' : ''}${isSelected ? ' is-selected' : ''}${
          isSunday ? ' is-sunday' : ''
        }`}
        onClick={() => {
          onSelect(toIso(new Date(year, month, day)));
          onClose();
        }}
      >
        {day}
      </button>
    );
  }

  return createPortal(
    <div className="cp-pin-cal-overlay" role="presentation" onClick={onClose}>
      <div
        className="cp-pin-cal-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-cal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-pin-cal-header">
          <h3 id="cp-pin-cal-title">날짜 선택</h3>
          <button type="button" className="cp-pin-modal-close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </header>

        <div className="cp-pin-cal-body">
          <div className="cp-pin-cal-nav">
            <button
              type="button"
              className="cp-pin-cal-nav-btn"
              onClick={() => setViewMonth(new Date(year, month - 1, 1))}
              aria-label="이전 달"
            >
              ‹
            </button>
            {editingYm ? (
              <div className="cp-pin-cal-ym-edit">
                <input
                  value={tempYear}
                  onChange={(e) => setTempYear(e.target.value)}
                  onBlur={finishYmEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') finishYmEdit();
                    if (e.key === 'Escape') setEditingYm(false);
                  }}
                  autoFocus
                  aria-label="연도"
                />
                <span>년</span>
                <input
                  value={tempMonth}
                  onChange={(e) => setTempMonth(e.target.value)}
                  onBlur={finishYmEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') finishYmEdit();
                    if (e.key === 'Escape') setEditingYm(false);
                  }}
                  aria-label="월"
                />
                <span>월</span>
              </div>
            ) : (
              <button
                type="button"
                className="cp-pin-cal-ym"
                onClick={() => {
                  setEditingYm(true);
                  setTempYear(String(year));
                  setTempMonth(String(month + 1));
                }}
              >
                {year}년 {month + 1}월
              </button>
            )}
            <button
              type="button"
              className="cp-pin-cal-nav-btn"
              onClick={() => setViewMonth(new Date(year, month + 1, 1))}
              aria-label="다음 달"
            >
              ›
            </button>
          </div>

          <button
            type="button"
            className="cp-pin-cal-today"
            onClick={() => {
              onSelect(toIso(new Date()));
              onClose();
            }}
          >
            오늘
          </button>

          <div className="cp-pin-cal-grid">
            {DAY_NAMES.map((name, i) => (
              <div key={name} className={`cp-pin-cal-dow${i === 0 ? ' is-sunday' : ''}`}>
                {name}
              </div>
            ))}
            {cells}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PointItemDateCalendarModal;
